import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { PAGES_ENDPOINTS } from '../../config/api';
import { UserDropdown } from './DropdownUser';
import { Button } from "../common/Button";
import { userNameWithInitial } from "../../api";
import { useAuth } from "../../contexts/AuthContext";
import { currentTenant } from "../../tenants";


export const Navbar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const { user, logout } = useAuth();
  const { companyName, companyDisplayName, logo, logoAlt } = currentTenant;
  const showTournamentRegistrationLink = !!currentTenant.registrationLink;
  
  const showAdminTab = (user && (user.can_manage_all || user.can_manage_content));

  const linkClass = (path: string) => {
    let unselectedLink = "font-bold bg-[var(--tenant-navbar-bg)] text-[var(--tenant-navbar-text)] hover:bg-[var(--tenant-navbar-hover-bg)] hover:text-[var(--tenant-navbar-hover-text)]";
    let selectedLink = "font-bold bg-[var(--tenant-navbar-active-bg)] text-[var(--tenant-navbar-active-text)]";
    return (location.pathname === path ? selectedLink : unselectedLink )
  }

  const handleLogout = () => {
    logout();
    setIsOpen(false);
    navigate(PAGES_ENDPOINTS.LOGIN);
  };

  const openTournamentRegistrationForm = () => {
    if (currentTenant.registrationLink) {
      window.open(currentTenant.registrationLink, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <nav className="bg-[var(--tenant-navbar-bg)] text-[var(--tenant-navbar-text)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16 items-center">
          
          {/* Logo */}
          <div className="flex-shrink-0">
            <Button 
              onClick={() => navigate(PAGES_ENDPOINTS.HOME) }
              className="font-bold text-[var(--tenant-navbar-text)] hover:bg-[var(--tenant-navbar-hover-bg)] hover:text-[var(--tenant-navbar-hover-text)]" 
              size="lg" variant="empty"
            >
              <span className="sm:hidden">{companyDisplayName}</span>
              <span className="hidden sm:inline-block">{companyName}</span>
              <img src={logo} alt={logoAlt} className="h-8 inline-block ml-4" />
            </Button>
          </div>

          {/* Menu desktop */}
          <div className="hidden md:flex space-x-4">
            <Button 
              onClick={() => navigate( PAGES_ENDPOINTS.HOME) } 
              className={linkClass(PAGES_ENDPOINTS.HOME)}
              size="lg" 
              variant="empty"
            >
              Home
            </Button>
            <Button 
              onClick={() => navigate( PAGES_ENDPOINTS.TORNEI) } 
              className={linkClass(PAGES_ENDPOINTS.TORNEI)}
              size="lg" 
              variant="empty"
            >
              Tornei
            </Button>
            {showTournamentRegistrationLink && (
              <Button
                onClick={openTournamentRegistrationForm}
                className={linkClass("")}
                size="lg"
                variant="empty"
              >
                Iscriviti
              </Button>
            )}
            {currentTenant.enableCalendar && (
              <Button
                onClick={() => navigate(PAGES_ENDPOINTS.CALENDARIO)}
                className={linkClass(PAGES_ENDPOINTS.CALENDARIO)}
                size="lg"
                variant="empty"
              >
                Calendario
              </Button>
            )}
            <Button 
              onClick={() => navigate( PAGES_ENDPOINTS.CLASSIFICA) }  
              className={linkClass(PAGES_ENDPOINTS.CLASSIFICA)}
              size="lg" 
              variant="empty"
            >
              Classifica
            </Button>
            <Button 
              onClick={() => navigate( PAGES_ENDPOINTS.ABOUT_US) }  
              className={linkClass(PAGES_ENDPOINTS.ABOUT_US)}
              size="lg" 
              variant="empty"
            >
              About us
            </Button>
            {showAdminTab && (
              <Button 
                onClick={() => navigate( PAGES_ENDPOINTS.ADMIN) }  
                className={linkClass(PAGES_ENDPOINTS.ADMIN)}
                size="lg" 
                variant="empty"
              >Admin</Button>
            )}
            <UserDropdown
              user={user}
              onLogout={handleLogout}
            />
          </div>

          {/* Bottone hamburger per mobile */}
          <div className="md:hidden">
            <Button
              onClick={() => setIsOpen(!isOpen)}
              variant="empty"
              className="font-bold text-[var(--tenant-navbar-text)] hover:bg-[var(--tenant-navbar-hover-bg)] hover:text-[var(--tenant-navbar-hover-text)]"
            >
              {/* Icona hamburger */}
              <svg
                className="h-6 w-6"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                {isOpen ? (
                  // X chiusura
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  // Icona hamburger
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </Button>
          </div>
        </div>
      </div>

      {/* Menu mobile */}
      {isOpen && (
        <div className="md:hidden bg-[var(--tenant-navbar-bg)] px-2 pt-2 pb-3 space-y-1">
          <Button
            variant="empty"
            className={linkClass(PAGES_ENDPOINTS.HOME) + " w-full"}
            onClick={() => {
              setIsOpen(false);
              navigate( PAGES_ENDPOINTS.HOME)
            }}
          >
            Home
          </Button>
          <Button
            variant="empty"
            className={linkClass(PAGES_ENDPOINTS.TORNEI) + " w-full"}
            onClick={() => {
              setIsOpen(false);
              navigate( PAGES_ENDPOINTS.TORNEI) 
            }}
          >
            Tornei
          </Button>
          {showTournamentRegistrationLink && (
            <Button
              variant="empty"
              className={linkClass("") + " w-full"}
              onClick={() => {
                setIsOpen(false);
                openTournamentRegistrationForm();
              }}
            >
              Iscriviti
            </Button>
          )}
          {currentTenant.enableCalendar && (
            <Button
              variant="empty"
              className={linkClass(PAGES_ENDPOINTS.CALENDARIO) + " w-full"}
              onClick={() => {
                setIsOpen(false);
                navigate(PAGES_ENDPOINTS.CALENDARIO);
              }}
            >
              Calendario
            </Button>
          )}
          <Button
            variant="empty"
            className={linkClass(PAGES_ENDPOINTS.CLASSIFICA) + " w-full"}
            onClick={() => {
              setIsOpen(false);
              navigate( PAGES_ENDPOINTS.CLASSIFICA);
            }}
          >
            Classifica
          </Button>
          <Button
            variant="empty"
            className={linkClass(PAGES_ENDPOINTS.ABOUT_US) + " w-full"}
            onClick={() => {
              setIsOpen(false);
              navigate( PAGES_ENDPOINTS.ABOUT_US);
            }}
          >
            About us
          </Button>
          {showAdminTab && (
            <Button 
              onClick={() => {
                setIsOpen(false);
                navigate( PAGES_ENDPOINTS.ADMIN)
              }}  
              className={linkClass(PAGES_ENDPOINTS.ADMIN) + " w-full"}
              variant="empty"
            >Admin</Button>
          )}
          { user ? (
            <div className="grid grid-cols-2 gap-2">
              <div className="flex flex-col">
                <Button
                    variant="primary" 
                    isOutlined={true}
                    onClick={() =>{
                      navigate( PAGES_ENDPOINTS.USER_PROFILE);
                    }}
                  >
                    {userNameWithInitial(user)}
                </Button>
              </div>
              <div className="flex flex-col">
                <Button
                  onClick={handleLogout}
                  variant="destructive"
                  className="border border-red-500"
                >
                  Logout
                </Button>
              </div>
            </div>
          ) : (
            <Button
              onClick={() => {
                setIsOpen(false); 
                navigate( PAGES_ENDPOINTS.LOGIN);
              }}
              variant="primary"
              isOutlined={true}
              className="w-full"
            >
              Login
            </Button>

          )}
        </div>
      )}
    </nav>
  );
};
